// vim: set ts=2 sts=2 sw=2 et:

import React from "react";
import { QRCodeSVG } from "qrcode.react";

import { canUseBrowserWebSocket, getBrowserPlatformLiveWebSocketUrl } from "../live/browserPlatformLiveClient";
import { rewriteUrlForLanAccess } from "../live/lanAccessibleUrl";
import {
  PlatformLiveAttempt,
  PlatformLiveAttemptClock,
  PlatformLiveResolvedAttempt,
  PlatformLiveServerMessage,
  PlatformLiveState,
} from "../live/platformLiveProtocol";
import { getEffectiveServerNowMs, getRemainingClockSeconds } from "../live/platformLiveClock";
import PlatformLiveAttemptHistory from "../live/PlatformLiveAttemptHistory";
import { recordAttemptAriaLabel, recordAttemptLabel, recordCategoryDataAttributes } from "../records/recordCategories";
import { JudgeContainerProps } from "./JudgeContainer";
import { JUDGE_POSITIONS, JudgePosition, JudgeVotes, JudgeVoteValue, resolveJudgeVotes } from "./resolveJudgeVotes";

import styles from "./JudgeView.module.scss";
import wrpLogo from "./wrp-logo.jpeg";

const JUDGE_LABELS: Record<JudgePosition, string> = {
  J1: "Juez Lateral Izquierdo",
  J2: "Juez Central",
  J3: "Juez Lateral Derecho",
};

const joinClassNames = (...classNames: Array<string | false | null | undefined>): string => {
  return classNames.filter((className): className is string => typeof className === "string").join(" ");
};

interface JudgeState {
  status: "connecting" | "connected" | "waiting" | "rejected" | "replaced" | "reconnecting";
  error: string | null;
  currentAttempt: PlatformLiveAttempt | null;
  lastResolvedAttempt: PlatformLiveResolvedAttempt | null;
  votes: JudgeVotes;
  lastVote: JudgeVoteValue | null;
  attemptClock: PlatformLiveAttemptClock | null;
  serverNowMs: number | null;
  clockReceivedAtMs: number | null;
  clockTick: number;
  replacementMenuOpen: boolean;
  qrModalOpen: boolean;
  // When true, the verdict is only final once the operator confirms — so judges may keep changing
  // their vote (the result panel is withheld) until the reveal arrives.
  manualVerdictConfirmation: boolean;
}

class JudgeView extends React.Component<JudgeContainerProps, JudgeState> {
  private socket: WebSocket | null = null;
  private clockTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;

  constructor(props: JudgeContainerProps) {
    super(props);
    this.state = {
      status: "connecting",
      error: null,
      currentAttempt: null,
      lastResolvedAttempt: null,
      votes: {},
      lastVote: null,
      attemptClock: null,
      serverNowMs: null,
      clockReceivedAtMs: null,
      clockTick: Date.now(),
      replacementMenuOpen: false,
      qrModalOpen: false,
      manualVerdictConfirmation: false,
    };
  }

  componentDidMount() {
    this.connect();
  }

  componentDidUpdate(prevProps: JudgeContainerProps) {
    if (
      prevProps.locationPathname !== this.props.locationPathname ||
      prevProps.locationSearch !== this.props.locationSearch ||
      prevProps.platformRef?.day !== this.props.platformRef?.day ||
      prevProps.platformRef?.platformId !== this.props.platformRef?.platformId ||
      prevProps.judgePosition !== this.props.judgePosition
    ) {
      this.disconnect();
      this.connect();
    }
  }

  componentWillUnmount() {
    this.disconnect();
    this.stopClockTicker();
  }

  connect = () => {
    if (this.props.platformRef === null || this.props.judgePosition === null) {
      this.setState({
        status: "rejected",
        error: "Invalid Judge URL.",
        currentAttempt: null,
        lastResolvedAttempt: null,
        votes: {},
        lastVote: null,
      });
      return;
    }

    if (!canUseBrowserWebSocket()) {
      this.setState({
        status: "waiting",
        error: "Browser WebSocket is unavailable.",
        currentAttempt: null,
        lastResolvedAttempt: null,
        votes: {},
        lastVote: null,
      });
      return;
    }

    const socket = new window.WebSocket(
      getBrowserPlatformLiveWebSocketUrl(this.props.platformRef, this.props.locationSearch),
    );
    this.socket = socket;
    this.setState({
      status: "connecting",
      error: null,
      currentAttempt: null,
      lastResolvedAttempt: null,
      votes: {},
      lastVote: null,
    });

    socket.onopen = () => {
      if (this.socket !== socket) {
        return;
      }
      if (this.props.platformRef === null || this.props.judgePosition === null) {
        return;
      }
      this.send({
        type: "judgeJoin",
        day: this.props.platformRef.day,
        platformId: this.props.platformRef.platformId,
        judgePosition: this.props.judgePosition,
      });
    };
    socket.onmessage = (event) => {
      if (this.socket !== socket) {
        return;
      }
      this.handleMessage(event.data);
    };
    socket.onclose = () => {
      if (this.socket !== socket) {
        return;
      }
      this.socket = null;
      this.scheduleReconnect();
    };
  };

  disconnect = () => {
    this.clearReconnectTimer();
    const socket = this.socket;
    this.socket = null;
    if (socket !== null) {
      socket.close();
    }
    this.stopClockTicker();
  };

  clearReconnectTimer = () => {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  // Auto-reconnect con backoff: el WS de la tablet del juez se cae cuando el host se duerme/reinicia
  // o hay un blip de WiFi. Reintentamos solos en vez de quedar colgados.
  scheduleReconnect = () => {
    this.clearReconnectTimer();
    if (this.state.status === "rejected" || this.state.status === "replaced") {
      return; // terminal: URL invalid or replaced by another tablet
    }
    const delay = Math.min(10000, 1000 * Math.pow(1.6, this.reconnectAttempts));
    this.reconnectAttempts += 1;
    this.setState({ status: "reconnecting" });
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  };

  reconnectNow = () => {
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    const socket = this.socket;
    this.socket = null;
    if (socket !== null) {
      try {
        socket.close();
      } catch (_error) {
        /* ignore */
      }
    }
    this.connect();
  };

  send = (message: unknown) => {
    if (this.socket === null || this.socket.readyState !== 1) {
      return;
    }
    this.socket.send(JSON.stringify(message));
  };

  handleMessage = (rawData: unknown) => {
    let message: PlatformLiveServerMessage;
    try {
      message = JSON.parse(String(rawData)) as PlatformLiveServerMessage;
    } catch (_error) {
      return;
    }

    if (message.type === "accepted" && message.role === "judge") {
      this.reconnectAttempts = 0;
      this.applyPlatformState(message.state);
      return;
    }
    if (message.type === "platformState") {
      this.applyPlatformState(message.state);
      return;
    }
    if (message.type === "attemptClosed") {
      if (this.state.currentAttempt?.key === message.attemptKey) {
        this.setState({
          currentAttempt: null,
          lastResolvedAttempt: null,
          votes: {},
          lastVote: null,
          status: "waiting",
          attemptClock: null,
          serverNowMs: null,
          clockReceivedAtMs: null,
        });
        this.stopClockTicker();
      }
      return;
    }
    if (message.type === "rejected") {
      // Con la sesión ya aceptada, un rechazo es de una acción puntual (un voto o una barra lista que ya no
      // corresponden): se avisa y se sigue conectado; el próximo estado del server lo limpia. Solo es
      // definitivo el rechazo al entrar (votación deshabilitada, tarima o posición inválidas).
      if (this.state.status === "connected" || this.state.status === "waiting") {
        this.setState({ error: message.reason });
        return;
      }
      this.stopClockTicker();
      this.setState({
        status: "rejected",
        error: message.reason,
        currentAttempt: null,
        lastResolvedAttempt: null,
        votes: {},
        lastVote: null,
        attemptClock: null,
        serverNowMs: null,
        clockReceivedAtMs: null,
      });
      return;
    }
    if (message.type === "replaced") {
      this.stopClockTicker();
      this.setState({
        status: "replaced",
        error: message.reason,
        currentAttempt: null,
        lastResolvedAttempt: null,
        votes: {},
        lastVote: null,
        attemptClock: null,
        serverNowMs: null,
        clockReceivedAtMs: null,
      });
    }
  };

  applyPlatformState = (platformState: PlatformLiveState) => {
    const votes = platformState.votes || {};
    const lastVote = this.props.judgePosition === null ? null : votes[this.props.judgePosition] || null;
    const receivedAtMs = Date.now();
    const serverNowMs = typeof platformState.serverNowMs === "number" ? platformState.serverNowMs : receivedAtMs;
    this.setState({
      status: platformState.currentAttempt === null ? "waiting" : "connected",
      error: null,
      currentAttempt: platformState.currentAttempt,
      lastResolvedAttempt: platformState.lastResolvedAttempt,
      votes,
      lastVote,
      attemptClock: platformState.attemptClock || null,
      serverNowMs,
      clockReceivedAtMs: receivedAtMs,
      clockTick: receivedAtMs,
      manualVerdictConfirmation: platformState.features?.manualVerdictConfirmation === true,
    });

    if (platformState.attemptClock !== null && platformState.attemptClock !== undefined) {
      this.startClockTicker();
    } else if (this.isRevealActiveFor(platformState.lastResolvedAttempt, serverNowMs, receivedAtMs, receivedAtMs)) {
      this.startClockTicker();
    } else {
      this.stopClockTicker();
    }
  };

  startClockTicker = () => {
    if (this.clockTimer !== null) {
      return;
    }
    this.clockTimer = window.setInterval(() => {
      this.setState({ clockTick: Date.now() }, () => {
        if (this.state.attemptClock === null && !this.isRevealActive()) {
          this.stopClockTicker();
        }
      });
    }, 1_000);
  };

  stopClockTicker = () => {
    if (this.clockTimer === null) {
      return;
    }
    window.clearInterval(this.clockTimer);
    this.clockTimer = null;
  };

  getCurrentClockSeconds = (): number | null => {
    const currentAttempt = this.getDisplayCurrentAttempt();
    if (
      this.isRevealActive() ||
      currentAttempt === null ||
      this.state.attemptClock?.attemptKey !== currentAttempt.key
    ) {
      return null;
    }

    return getRemainingClockSeconds(
      {
        attemptClock: this.state.attemptClock,
        serverNowMs: this.state.serverNowMs,
        receivedAtMs: this.state.clockReceivedAtMs,
      },
      this.state.clockTick,
    );
  };

  isRevealActiveFor = (
    lastResolvedAttempt: PlatformLiveResolvedAttempt | null,
    serverNowMs: number | null,
    receivedAtMs: number | null,
    clientNowMs: number,
  ): boolean => {
    if (typeof lastResolvedAttempt?.revealUntilMs !== "number") {
      return false;
    }

    const effectiveServerNowMs = getEffectiveServerNowMs({ serverNowMs, receivedAtMs }, clientNowMs);
    return effectiveServerNowMs !== null && effectiveServerNowMs < lastResolvedAttempt.revealUntilMs;
  };

  isRevealActive = (): boolean => {
    // Fixed 2s reveal window — the operator's "Siguiente" hold does not extend it.
    return this.isRevealActiveFor(
      this.state.lastResolvedAttempt,
      this.state.serverNowMs,
      this.state.clockReceivedAtMs,
      this.state.clockTick,
    );
  };

  getDisplayCurrentAttempt = (): PlatformLiveAttempt | null => {
    if (this.isRevealActive()) {
      return this.state.lastResolvedAttempt?.attempt || null;
    }
    return this.state.currentAttempt;
  };

  getResultRevealSeconds = (): number | null => {
    if (!this.isRevealActive() || typeof this.state.lastResolvedAttempt?.revealUntilMs !== "number") {
      return null;
    }

    const effectiveServerNowMs = getEffectiveServerNowMs(
      {
        serverNowMs: this.state.serverNowMs,
        receivedAtMs: this.state.clockReceivedAtMs,
      },
      this.state.clockTick,
    );
    if (effectiveServerNowMs === null) {
      return null;
    }

    return Math.ceil(Math.max(0, this.state.lastResolvedAttempt.revealUntilMs - effectiveServerNowMs) / 1_000);
  };

  canVote = (): boolean => {
    return (
      this.state.status === "connected" &&
      !this.isRevealActive() &&
      this.state.currentAttempt !== null &&
      this.state.attemptClock?.attemptKey === this.state.currentAttempt.key
    );
  };

  // Only the central Judge (J2) may start the bar-ready clock, and only while there IS a current attempt
  // whose clock has NOT been started yet (i.e. the operator hasn't already pressed "Barra lista").
  isCentralJudge = (): boolean => this.props.judgePosition === "J2";

  canSetBarReady = (): boolean => {
    return (
      this.isCentralJudge() &&
      this.state.status === "connected" &&
      !this.isRevealActive() &&
      this.state.currentAttempt !== null &&
      this.state.attemptClock?.attemptKey !== this.state.currentAttempt.key
    );
  };

  handleVote = (vote: JudgeVoteValue) => {
    if (this.props.platformRef === null || this.state.currentAttempt === null || !this.canVote()) {
      return;
    }

    this.setState((state) => {
      if (this.props.judgePosition === null) {
        return { lastVote: vote, votes: state.votes };
      }
      return {
        lastVote: vote,
        votes: {
          ...state.votes,
          [this.props.judgePosition]: vote,
        },
      };
    });
    this.send({
      type: "judgeVote",
      day: this.props.platformRef.day,
      platformId: this.props.platformRef.platformId,
      attemptKey: this.state.currentAttempt.key,
      vote,
    });
  };

  handleBarReady = () => {
    if (this.props.platformRef === null || this.state.currentAttempt === null || !this.canSetBarReady()) {
      return;
    }
    this.send({
      type: "barReady",
      day: this.props.platformRef.day,
      platformId: this.props.platformRef.platformId,
      attemptKey: this.state.currentAttempt.key,
    });
  };

  handleChangeVote = () => {
    if (this.props.judgePosition === null || !this.canVote()) {
      return;
    }

    const judgePosition = this.props.judgePosition;
    this.setState((state) => {
      const votes = { ...state.votes };
      delete votes[judgePosition];
      return { lastVote: null, votes };
    });
  };

  handleToggleReplacementMenu = () => {
    this.setState((state) => ({ replacementMenuOpen: !state.replacementMenuOpen }));
  };

  handleOpenQrModal = () => {
    this.setState({ qrModalOpen: true, replacementMenuOpen: false });
  };

  handleCloseQrModal = () => {
    this.setState({ qrModalOpen: false });
  };

  getReplacementUrl = (): string => {
    const searchParams = new URLSearchParams(this.props.locationSearch);
    searchParams.delete("t");
    const search = searchParams.toString();
    const pathAndSearch = `${this.props.locationPathname}${search.length > 0 ? `?${search}` : ""}`;
    if (typeof window === "undefined" || window.location === undefined) {
      return pathAndSearch;
    }

    const currentUrl = new URL(pathAndSearch, window.location.origin).toString();
    return rewriteUrlForLanAccess(currentUrl, this.props.lanHost);
  };

  getAttemptLabel = (attempt: PlatformLiveAttempt): string => {
    return `${attempt.lift}${attempt.attempt}`;
  };

  getStatusLabel = (): string => {
    if (this.state.status === "connected") {
      return "Conectado";
    }
    if (this.state.status === "connecting") {
      return "Conectando";
    }
    if (this.state.status === "rejected") {
      return "Rechazado";
    }
    if (this.state.status === "replaced") {
      return "Reemplazado";
    }
    if (this.state.status === "reconnecting") {
      return "Reconectando…";
    }
    return "Esperando";
  };

  formatClock = (seconds: number): string => {
    return `0:${String(Math.max(0, seconds)).padStart(2, "0")}`;
  };

  getVoteLabel = (vote: JudgeVoteValue | null): string => {
    if (vote === "good") {
      return "Válido";
    }
    if (vote === "noLift") {
      return "Nulo";
    }
    return "Pendiente";
  };

  getVoteClassName = (vote: JudgeVoteValue | null): string => {
    if (vote === "good") {
      return styles.voteGood;
    }
    if (vote === "noLift") {
      return styles.voteBad;
    }
    return styles.votePending;
  };

  getDisplayVotes = (): JudgeVotes => {
    if (this.isRevealActive() && this.state.lastResolvedAttempt?.judgeLights !== undefined) {
      return JUDGE_POSITIONS.reduce<JudgeVotes>((votes, position) => {
        const light = this.state.lastResolvedAttempt?.judgeLights?.[position];
        if (light === "white") {
          votes[position] = "good";
        } else if (light === "red") {
          votes[position] = "noLift";
        }
        return votes;
      }, {});
    }

    const votes = { ...this.state.votes };
    if (this.props.judgePosition !== null && this.state.lastVote !== null) {
      votes[this.props.judgePosition] = this.state.lastVote;
    }
    return votes;
  };

  getVisibleResult = (votes: JudgeVotes, revealResult: JudgeVoteValue | null): JudgeVoteValue | null => {
    if (revealResult !== null) {
      return revealResult;
    }

    // Manual-confirmation mode: the verdict is NOT final until the operator confirms (which triggers the
    // reveal handled above). Until then keep returning null so the vote buttons stay and judges can
    // still change their vote.
    if (this.state.manualVerdictConfirmation) {
      return null;
    }

    const allVotesIn = JUDGE_POSITIONS.every((position) => votes[position] !== undefined);
    if (!allVotesIn) {
      return null;
    }

    return resolveJudgeVotes(votes) === true ? "good" : "noLift";
  };

  renderTimer = (remainingSeconds: number | null, resultRevealSeconds: number | null, showResult: boolean) => {
    const seconds = resultRevealSeconds === null ? remainingSeconds : resultRevealSeconds;
    if (seconds === null) {
      return null;
    }

    const danger = remainingSeconds !== null && remainingSeconds <= 10 && !showResult;
    return (
      <div
        className={joinClassNames(styles.timerPanel, danger && styles.timerDanger)}
        aria-label={resultRevealSeconds === null ? "Attempt Clock" : "Result Reveal Clock"}
      >
        <span className={styles.timerLabel}>{resultRevealSeconds === null ? "Tiempo" : "Decisión"}</span>
        <span className={styles.timerValue}>{this.formatClock(seconds)}</span>
      </div>
    );
  };

  // Un juez no ve cómo (ni si) votaron los otros: solo su propio voto, antes y después del veredicto. El
  // resultado sigue saliendo de los tres votos (getVisibleResult), que el server manda a todos los roles.
  getOwnPositions = (): ReadonlyArray<JudgePosition> =>
    JUDGE_POSITIONS.filter((position) => position === this.props.judgePosition);

  renderRefDots = (votes: JudgeVotes) => {
    return (
      <div className={styles.refDots} aria-label="Judge Vote Lights">
        {this.getOwnPositions().map((position) => {
          const vote = votes[position] || null;
          const mine = position === this.props.judgePosition;
          return (
            <div className={styles.refDotSlot} key={position}>
              <div
                className={joinClassNames(
                  styles.refDot,
                  vote === "good" && styles.refDotGood,
                  vote === "noLift" && styles.refDotBad,
                  mine && styles.refDotMine,
                )}
                aria-label={`${JUDGE_LABELS[position]} ${this.getVoteLabel(vote)}`}
              />
              <span className={joinClassNames(styles.refDotLabel, mine && styles.refDotLabelMine)}>
                {mine ? "VOS" : JUDGE_LABELS[position]}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  renderVoteButton = (vote: JudgeVoteValue, canVote: boolean, myVote: JudgeVoteValue | null) => {
    const good = vote === "good";
    const active = myVote === vote;
    const dim = myVote !== null && !active;
    return (
      <button
        type="button"
        aria-label={good ? "Judge Good Lift" : "Judge No Lift"}
        className={joinClassNames(
          styles.callButton,
          good ? styles.goodButton : styles.badButton,
          active && styles.voteActive,
          dim && styles.voteDim,
        )}
        disabled={!canVote}
        onClick={() => this.handleVote(vote)}
      >
        <span className={styles.callIcon}>{good ? "✓" : "✗"}</span>
        <span className={styles.callText}>{good ? "Válido" : "Nulo"}</span>
      </button>
    );
  };

  // El juez tampoco ve el veredicto: con su voto y el resultado deduciría los votos ajenos. El panel avisa que
  // la votación se cerró, sin colores de resultado.
  renderResult = (votes: JudgeVotes) => {
    return (
      <div className={styles.resultPanel}>
        <div className={styles.resultLights} aria-label="Judge Result Lights">
          {this.getOwnPositions().map((position) => {
            const vote = votes[position] || null;
            // Sin voto propio no hay luz: la luz apagada de este panel se ve roja.
            if (vote === null) return null;
            return (
              <div
                key={position}
                className={joinClassNames(styles.resultLight, vote === "good" && styles.resultLightGood)}
                aria-label={`${JUDGE_LABELS[position]} result ${this.getVoteLabel(vote)}`}
              />
            );
          })}
        </div>
        <div className={styles.resultTitle}>Votación cerrada</div>
      </div>
    );
  };

  renderDecisionArea = (votes: JudgeVotes, visibleResult: JudgeVoteValue | null, canVote: boolean) => {
    if (visibleResult !== null) {
      return this.renderResult(votes);
    }

    const myVote = this.props.judgePosition === null ? null : votes[this.props.judgePosition] || null;
    return (
      <div className={joinClassNames(styles.decisionArea, styles.decisionAreaHasVoteButtons)}>
        {this.canSetBarReady() ? (
          <button
            type="button"
            aria-label="Judge Bar Ready"
            className={styles.barReadyButton}
            onClick={this.handleBarReady}
          >
            Barra lista
          </button>
        ) : null}
        {this.renderVoteButton("good", canVote, myVote)}
        {this.renderVoteButton("noLift", canVote, myVote)}
        <div className={styles.lowerControls}>
          {this.renderRefDots(votes)}
          <div className={styles.lowerSpacer} />
          {myVote === null ? null : (
            <button type="button" className={styles.changeButton} onClick={this.handleChangeVote}>
              ↺ Cambiar
            </button>
          )}
        </div>
        {myVote === null ? null : (
          <div className={styles.registered}>
            {this.state.manualVerdictConfirmation
              ? "Voto registrado · podés cambiarlo hasta la confirmación"
              : "Voto registrado · esperando jueces"}
          </div>
        )}
      </div>
    );
  };

  renderQrModal = () => {
    if (!this.state.qrModalOpen || this.props.judgePosition === null || this.props.platformRef === null) {
      return null;
    }

    const qrLabel = `QR de reemplazo ${JUDGE_LABELS[this.props.judgePosition]}`;
    return (
      <div
        className={styles.replacementOverlay}
        role="dialog"
        aria-modal="true"
        aria-label={qrLabel}
        onClick={this.handleCloseQrModal}
      >
        <div className={styles.replacementModal} onClick={(event) => event.stopPropagation()}>
          <div className={styles.replacementQr} data-replacement-url={this.getReplacementUrl()}>
            <QRCodeSVG
              value={this.getReplacementUrl()}
              size={280}
              level="M"
              bgColor="#ffffff"
              fgColor="#15100e"
              includeMargin={true}
            />
          </div>
          <div className={styles.replacementTitle}>Reemplazar {JUDGE_LABELS[this.props.judgePosition]}</div>
          <div className={styles.replacementBody}>
            Escaneá este QR desde otro dispositivo. Al conectar, esta posición reemplaza al dispositivo anterior.
          </div>
          <div className={styles.replacementMeta}>
            Día {this.props.platformRef.day} · Plataforma {this.props.platformRef.platformId}
          </div>
          <button type="button" className={styles.replacementCloseButton} onClick={this.handleCloseQrModal}>
            Cerrar
          </button>
        </div>
      </div>
    );
  };

  renderReplacementNotice = () => {
    if (this.state.status !== "replaced" || this.props.judgePosition === null) {
      return null;
    }

    return (
      <div className={styles.replacedNotice} role="status">
        Este dispositivo fue reemplazado por otra conexión para {JUDGE_LABELS[this.props.judgePosition]}.
      </div>
    );
  };

  renderInvalid = () => {
    return (
      <div className={styles.judgePage}>
        <div className={styles.invalid} role="alert">
          <h1>Invalid Judge URL</h1>
          <p>The Judge Position, day, or platform is not valid.</p>
        </div>
      </div>
    );
  };

  render() {
    if (this.props.platformRef === null || this.props.judgePosition === null) {
      return this.renderInvalid();
    }

    const currentAttempt = this.getDisplayCurrentAttempt();
    const canVote = this.canVote();
    const remainingSeconds = this.getCurrentClockSeconds();
    const revealResult = this.isRevealActive() ? this.state.lastResolvedAttempt?.result || null : null;
    const resultRevealSeconds = this.getResultRevealSeconds();
    const displayVotes = this.getDisplayVotes();
    const visibleResult = this.getVisibleResult(displayVotes, revealResult);
    // Sin voto propio queda "Pendiente": caer al resultado le contaría el veredicto.
    const myVote = this.props.judgePosition === null ? null : displayVotes[this.props.judgePosition] || null;
    const statusText = `${this.getStatusLabel()}${this.state.error === null ? "" : `: ${this.state.error}`}`;
    const recordCategory = currentAttempt?.recordCategory;

    return (
      <div className={styles.judgePage}>
        <header className={styles.brandHeader}>
          <img src={wrpLogo} alt="WRPF" className={styles.crest} />
          <span className={styles.brandTitle}>JURADO</span>
          <div className={styles.headerSpacer} />
          <div className={styles.menuWrapper}>
            <button
              type="button"
              className={styles.menuButton}
              aria-label="Menú"
              aria-expanded={this.state.replacementMenuOpen}
              onClick={this.handleToggleReplacementMenu}
            >
              <span className={styles.menuBars} aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
            {this.state.replacementMenuOpen ? (
              <div className={styles.dropdownMenu} role="menu">
                <button type="button" className={styles.dropdownItem} role="menuitem" onClick={this.handleOpenQrModal}>
                  Reemplazar Juez
                </button>
              </div>
            ) : null}
          </div>
        </header>

        <div className={styles.contextRow}>
          <span className={styles.positionLabel}>
            {JUDGE_LABELS[this.props.judgePosition]} <span className={styles.youLabel}>· VOS</span>
          </span>
          <span className={styles.platformLock}>
            Día {this.props.platformRef.day} · Plataforma {this.props.platformRef.platformId}
          </span>
        </div>

        {this.renderQrModal()}

        <main className={styles.main}>
          <div
            className={joinClassNames(
              styles.judgeCard,
              currentAttempt?.recordAttempt === true && styles.judgeCardRecord,
            )}
            {...recordCategoryDataAttributes(recordCategory)}
          >
            <div
              aria-label="Judge Connection Status"
              className={joinClassNames(
                styles.connection,
                this.state.status === "connecting" && styles.statusConnecting,
                this.state.status === "reconnecting" && styles.statusConnecting,
                this.state.status === "waiting" && styles.statusWaiting,
                this.state.status === "rejected" && styles.statusRejected,
                this.state.status === "replaced" && styles.statusReplaced,
              )}
            >
              <span className={styles.statusDot} />
              {statusText}
              {this.state.status === "reconnecting" ||
              this.state.status === "rejected" ||
              this.state.status === "replaced" ? (
                <button type="button" className="btn btn-warning btn-sm ml-2" onClick={this.reconnectNow}>
                  Reconectar
                </button>
              ) : null}
            </div>
            <div className={styles.judgeCardBody}>
              <div className={styles.lifterName}>
                {currentAttempt === null
                  ? this.state.lastResolvedAttempt != null
                    ? "Esperando al siguiente atleta"
                    : "Esperando intento actual"
                  : currentAttempt.lifterName}
              </div>
              {currentAttempt === null ? (
                <div className={styles.attemptRow}>
                  <span className={styles.weight}>Sin intento activo</span>
                </div>
              ) : (
                <div className={styles.attemptRow}>
                  <span className={styles.attemptBadge}>{this.getAttemptLabel(currentAttempt)}</span>
                  <span className={styles.weight}>{currentAttempt.weightKg.toFixed(1)} kg</span>
                  {currentAttempt.recordAttempt === true ? (
                    <span
                      className={styles.recordBadge}
                      aria-label={recordAttemptAriaLabel(recordCategory)}
                      {...recordCategoryDataAttributes(recordCategory)}
                    >
                      {recordAttemptLabel(recordCategory)}
                    </span>
                  ) : null}
                </div>
              )}

              <PlatformLiveAttemptHistory
                attempt={currentAttempt}
                variant="judge"
                dimmed={this.isRevealActive()}
                // Sin reveal: la celda del intento recién juzgado no se pinta con el veredicto.
              />

              {this.renderTimer(remainingSeconds, resultRevealSeconds, visibleResult !== null)}

              <div className={styles.voteLine}>
                Tu voto: <b className={this.getVoteClassName(myVote || null)}>{this.getVoteLabel(myVote || null)}</b>
              </div>
            </div>
            {this.renderReplacementNotice()}
          </div>
          {this.renderDecisionArea(displayVotes, visibleResult, canVote)}
        </main>
      </div>
    );
  }
}

export default JudgeView;
